import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

export default function Subscriptions() {
  const [data, setData] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [renewModal, setRenewModal] = useState(null);
  const [renewForm, setRenewForm] = useState({ new_expiry: '', amount: '', plan: '' });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [sRes, rRes] = await Promise.all([
        api.get('/subscriptions'),
        api.get('/reminders?pending=true'),
      ]);
      setData(sRes.data.data);
      setReminders(rRes.data.data || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRenew = async () => {
    if (!renewForm.new_expiry) return;
    setSaving(true);
    try {
      await api.post(`/subscriptions/${renewModal.code}/renew`, renewForm);
      setRenewModal(null);
      fetchData();
    } catch {} finally { setSaving(false); }
  };

  const markReminderSent = async (id) => {
    try { await api.patch(`/reminders/${id}/sent`); setReminders(r => r.filter(x => x.id !== id)); } catch {}
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  const subs = data?.subscriptions || [];
  const summary = data?.summary || {};
  const expired = subs.filter(s => s.is_expired);
  const expiringSoon = subs.filter(s => s.is_expiring_soon);
  const healthy = subs.filter(s => !s.is_expired && !s.is_expiring_soon);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Subscription Management</h2>
        <p className="text-sm text-gray-500 mt-0.5">Track and manage tenant subscriptions, renewals, and reminders</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
          <div className="text-xs text-gray-500 uppercase font-medium">Total Revenue</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">₹{((summary.total_active_revenue || 0) / 1000).toFixed(0)}K</div>
        </div>
        <div className="bg-red-50 rounded-xl border border-red-100 p-4">
          <div className="text-xs text-red-600 uppercase font-medium">Expired</div>
          <div className="text-2xl font-bold text-red-700 mt-1">{summary.expired}</div>
        </div>
        <div className="bg-amber-50 rounded-xl border border-amber-100 p-4">
          <div className="text-xs text-amber-600 uppercase font-medium">Expiring Soon</div>
          <div className="text-2xl font-bold text-amber-700 mt-1">{summary.expiring_soon}</div>
        </div>
        <div className="bg-blue-50 rounded-xl border border-blue-100 p-4">
          <div className="text-xs text-blue-600 uppercase font-medium">Pending Reminders</div>
          <div className="text-2xl font-bold text-blue-700 mt-1">{summary.pending_reminders}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 border-b border-gray-200">
        {[['overview', 'All Subscriptions'], ['expired', `Expired (${expired.length})`], ['expiring', `Expiring Soon (${expiringSoon.length})`], ['reminders', `Reminders (${reminders.length})`]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} className={`pb-3 text-sm font-medium border-b-2 transition-colors ${tab === key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{label}</button>
        ))}
      </div>

      {/* Content */}
      {tab === 'reminders' ? (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {reminders.length === 0 ? <div className="text-center py-10 text-gray-400 text-sm">No pending reminders</div> : (
            <div className="divide-y divide-gray-50">
              {reminders.map(r => (
                <div key={r.id} className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${r.reminder_type.includes('1d') || r.reminder_type === 'expired' ? 'bg-red-500' : r.reminder_type.includes('7d') ? 'bg-amber-500' : 'bg-blue-500'}`} />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{r.tenant_name || r.tenant_code}: {r.message}</div>
                      <div className="text-xs text-gray-400">Scheduled: {new Date(r.scheduled_date).toLocaleDateString()} &middot; Type: {r.reminder_type}</div>
                    </div>
                  </div>
                  <button onClick={() => markReminderSent(r.id)} className="text-xs text-blue-600 hover:text-blue-700 font-medium px-3 py-1 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">Mark Sent</button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase">
                {['Tenant', 'Plan', 'Start', 'Expiry', 'Days Left', 'Amount', 'Cycle', 'Auto Renew', 'Actions'].map(h => <th key={h} className="px-4 py-3 text-left">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-gray-50">
                {(tab === 'expired' ? expired : tab === 'expiring' ? expiringSoon : tab === 'overview' ? subs : healthy).map(s => (
                  <tr key={s.code} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3"><div className="text-sm font-medium text-gray-900">{s.name}</div><div className="text-xs text-gray-400">{s.code}</div></td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${s.subscription_plan === 'premium' ? 'bg-indigo-100 text-indigo-700' : s.subscription_plan === 'basic' ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-700'}`}>{s.subscription_plan}</span></td>
                    <td className="px-4 py-3 text-sm text-gray-600">{s.subscription_start ? new Date(s.subscription_start).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{s.subscription_expiry ? new Date(s.subscription_expiry).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-3">
                      {s.days_until_expiry != null ? (
                        <span className={`text-sm font-bold ${s.days_until_expiry < 0 ? 'text-red-600' : s.days_until_expiry <= 30 ? 'text-amber-600' : 'text-green-600'}`}>
                          {s.days_until_expiry < 0 ? 'EXPIRED' : s.days_until_expiry}
                        </span>
                      ) : <span className="text-xs text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">₹{parseFloat(s.subscription_amount || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm capitalize text-gray-600">{s.billing_cycle}</td>
                    <td className="px-4 py-3">{s.auto_renew ? <span className="text-xs text-green-600 font-medium">Yes</span> : <span className="text-xs text-gray-400">No</span>}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => { setRenewModal(s); setRenewForm({ new_expiry: '', amount: s.subscription_amount || '', plan: s.subscription_plan }); }} className="text-xs text-blue-600 hover:text-blue-700 font-medium px-3 py-1 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">Renew</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Renew Modal */}
      {renewModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"><div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
          <h3 className="text-lg font-bold text-gray-900">Renew Subscription</h3>
          <p className="text-sm text-gray-500 mt-1">{renewModal.name} ({renewModal.code})</p>
          <div className="space-y-3 mt-4">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">New Expiry Date *</label><input type="date" value={renewForm.new_expiry} onChange={e => setRenewForm(p => ({ ...p, new_expiry: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Amount (₹)</label><input type="number" value={renewForm.amount} onChange={e => setRenewForm(p => ({ ...p, amount: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Plan</label>
              <select value={renewForm.plan} onChange={e => setRenewForm(p => ({ ...p, plan: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"><option value="basic">Basic</option><option value="standard">Standard</option><option value="premium">Premium</option></select></div>
          </div>
          <div className="flex justify-end space-x-3 mt-5">
            <button onClick={() => setRenewModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
            <button onClick={handleRenew} disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{saving ? 'Saving...' : 'Renew'}</button>
          </div>
        </div></div>
      )}
    </div>
  );
}
